import React, { useState, useRef, useEffect } from 'react'
import classnames from 'classnames/bind'
import { useTranslation } from 'next-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper'
import { format, formatISO } from 'date-fns'
import isToday from 'date-fns/isToday'
import { useAppSelector, useAppDispatch } from '@/redux/hooks'

// Components
import Skeleton from 'react-loading-skeleton'
import ProductCard from '@/components/shared/ProductCard'
import Icon from '@/assets/icons'

// Libs
import { useRouter } from 'next/router'
import { setProductCardSize } from '@/redux/reducers/recentLiveStreaming'
import { resetCalendarData } from '@/redux/reducers/calendar'
import { handleGtmClickLiveCalendar, handleGtmViewItemListCustomized } from '@/methods/handleGtmEvent'

// Methods
import { goToCalendarPage } from '@/methods/goToPage'

// Styles
import styles from './style.module.scss'
import { isUndefined, isNull, isEmpty } from 'lodash-es'

// constants
import { GTM_LOCATION, GtmClickLiveCalendarClickText, GtmItemListName } from '@/constants/gtmEvent'
import { COMPONENT_NAMES } from '@/constants/componentNames'

const cx = classnames.bind(styles)

const RecentLiveStreaming = (props: any) => {
  const { data, categoryMenuDataMap } = props
  const router = useRouter()
  const { query } = router
  const swiperRef: any = useRef(null)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { productCardWidth } = useAppSelector((state) => state.recentLiveStreaming)
  const { category_id } = query
  const dateList = data.map((item: any) => item.liveTime)
  const [activeIndex, setActiveIndex] = useState(0)
  const [hideSkeleton, setHideSkeleton] = useState(false)
  const [hideProductCardListSkeleton, setHideProductCardListSkeleton] = useState(false)
  const [slideIsEnd, setSlideIsEnd] = useState(false)

  const getDisplayDateIndex = (dateList: string[]) => {
    const result: any = []
    const checkedDate: any = []

    dateList.forEach((item: string, index: number) => {
      const NOT_MATCHED = -1
      const isMatched = checkedDate.indexOf(format(new Date(formatISO(new Date(item))), 'M/d'))

      if (isMatched === NOT_MATCHED) {
        result.push(index)
        checkedDate.push(format(new Date(formatISO(new Date(item))), 'M/d'))
      }
    })

    return result
  }

  const displayDateIndex = getDisplayDateIndex(dateList)

  const getProductCardWidth = () => {
    const intervalId = setInterval(() => {
      const productCardEl = document.querySelector('[data-selector-id="product-card"]')
      const width = productCardEl?.clientWidth

      if (!isNull(width) && !isUndefined(width) && width !== 0) {
        clearInterval(intervalId)
        dispatch(setProductCardSize({ width }))
        setHideSkeleton(false)
      }
    }, 200)
  }

  const handleSendTrackEvent = () => {
    handleGtmClickLiveCalendar({
      location: GTM_LOCATION.MALL,
      clickText: t(GtmClickLiveCalendarClickText.MORE),
    })
  }

  const handleClick = () => {
    handleSendTrackEvent()
    dispatch(resetCalendarData({}))
    goToCalendarPage(router, { query: { category_id, date: format(new Date(formatISO(new Date(dateList[activeIndex]))), 'yyyyMMdd') } })
  }

  const getGaEventViewItemListPayload = (productList: any) => {
    const result: any = {
      itemListName: '',
      items: [],
    }

    productList?.forEach((product: any) => {
      result.itemListName = GtmItemListName.CALENDAR

      result.items.push({
        itemName: product?.productInfo?.productName,
        itemId: product?.productInfo?.productId,
        itemBrand: product?.productInfo?.merchantName,
        itemCategory: product?.productInfo?.categoryName,
        itemListName: GtmItemListName.CALENDAR,
      })
    })

    return result
  }

  useEffect(() => {
    const skeletonTimerId = window.setTimeout(() => {
      setHideSkeleton(true)
    }, 800)

    const productCardListSkeletonTimerId = window.setTimeout(() => {
      setHideProductCardListSkeleton(true)
    }, 100)

    return () => {
      window.clearTimeout(skeletonTimerId)
      window.clearTimeout(productCardListSkeletonTimerId)
    }
  }, [category_id])

  useEffect(() => {
    if (!isEmpty(category_id)) {
      if (productCardWidth === 0) {
        getProductCardWidth()
      }

      setActiveIndex(0)
    }
  }, [category_id, productCardWidth])

  // 發送 GA 事件：view_item_list
  useEffect(() => {
    const payload = getGaEventViewItemListPayload(data)

    handleGtmViewItemListCustomized(payload)
  }, [data])

  return (
    <div className={cx('recent-live-streaming')}>
      <div className={cx('recent-live-streaming__header')}>
        <div className={cx('recent-live-streaming__header-left')}>
          <div className={cx('recent-live-streaming__title')}>
            <div
              className={cx('recent-live-streaming__title-content', {
                'recent-live-streaming__title-content--active': productCardWidth > 0 && hideSkeleton,
              })}
            >
              {t('Recent_Live_Streaming_Title')}
            </div>
            <div
              className={cx('recent-live-streaming__title-skeleton', {
                'recent-live-streaming__title-skeleton--active': productCardWidth > 0 && !hideSkeleton,
              })}
            >
              <Skeleton width={'4em'} />
            </div>
          </div>
          <div className={cx('recent-live-streaming__subtitle')}>
            <div
              className={cx('recent-live-streaming__subtitle-content', {
                'recent-live-streaming__subtitle-content--active': productCardWidth > 0 && hideSkeleton,
              })}
            >
              👉 {t('LiveStreamingPreview_Subtitle')}
            </div>
            <div
              className={cx('recent-live-streaming__subtitle-skeleton', {
                'recent-live-streaming__subtitle-skeleton--active': productCardWidth > 0 && !hideSkeleton,
              })}
            >
              <Skeleton width={'10em'} />
            </div>
          </div>
        </div>
        <div className={cx('recent-live-streaming__header-right')}>
          <div
            onClick={handleClick}
            className={cx('recent-live-streaming__action-more-content', {
              'recent-live-streaming__action-more-content--active': productCardWidth > 0 && hideSkeleton,
            })}
          >
            {t('Common_More')}
          </div>

          <div
            className={cx('recent-live-streaming__action-more-skeleton', {
              'recent-live-streaming__action-more-skeleton--active': productCardWidth > 0 && !hideSkeleton,
            })}
          >
            <Skeleton width={'2em'} />
          </div>
        </div>
      </div>

      {productCardWidth > 0 && (
        <>
          <Swiper
            onInit={(ref) => (swiperRef.current = ref)}
            onAfterInit={(swiper) => {
              if (swiper.isEnd) {
                setSlideIsEnd(true)
              } else {
                setSlideIsEnd(false)
              }
            }}
            onSlideChange={(swiper) => {
              setActiveIndex(swiper.activeIndex)

              if (!swiper.isEnd) {
                setSlideIsEnd(false)
              }
            }}
            className={cx('recent-live-streaming__list', { 'recent-live-streaming__list--active': hideProductCardListSkeleton })}
            slidesPerView={'auto'}
            spaceBetween={10}
            allowTouchMove={true}
            navigation={{
              prevEl: '.navigation-btn-prev',
              nextEl: '.navigation-btn-next',
            }}
            modules={[Navigation]}
            noSwiping
            noSwipingClass='no-swipe'
          >
            {data?.map((data: any, index: number) => (
              <SwiperSlide
                key={index}
                className={cx('recent-live-streaming__slide', {
                  'recent-live-streaming__slide--visible':
                    !isToday(new Date(formatISO(new Date(data?.liveTime)))) && activeIndex !== index && displayDateIndex.includes(index),
                })}
              >
                <div
                  className={cx('recent-live-streaming__date', {
                    'recent-live-streaming__date--visible':
                      !isToday(new Date(formatISO(new Date(data?.liveTime)))) && displayDateIndex.includes(index),
                  })}
                >
                  <div className={cx('recent-live-streaming__date-content', { 'recent-live-streaming__date-content--active': hideSkeleton })}>
                    <div className={cx('recent-live-streaming__date-content-wrapper')}>
                      {isToday(new Date(formatISO(new Date(data?.liveTime))))
                        ? t('Common_Today')
                        : format(new Date(formatISO(new Date(data.liveTime))), 'M/d')}
                    </div>
                  </div>
                  <div className={cx('recent-live-streaming__date-skeleton', { 'recent-live-streaming__date-skeleton--active': !hideSkeleton })}>
                    <Skeleton width={'3em'} />
                  </div>
                </div>
                <div className={cx('recent-live-streaming__product-card-wrapper')}>
                  <div
                    className={cx('recent-live-streaming__product-card-content', {
                      'recent-live-streaming__product-card-content--active': hideSkeleton,
                    })}
                  >
                    <ProductCard
                      className={cx('recent-live-streaming__product-card')}
                      inlineStyle={{ width: `${productCardWidth}px` }}
                      {...data?.productInfo}
                      isPromo
                      showSaleQuantity={false}
                      gtmItemEventParams={{
                        itemListName: `${GtmItemListName.CATEGORY}_${categoryMenuDataMap[String(category_id)]?.name}`,
                        itemListId: `category_id=${category_id}`,
                      }}
                      useFrom={COMPONENT_NAMES.RECENT_LIVE_STREAMING}
                    />
                  </div>

                  <div
                    className={cx('recent-live-streaming__product-card-skeleton', {
                      'recent-live-streaming__product-card-skeleton--active': !hideSkeleton,
                    })}
                  >
                    <Skeleton borderRadius={10} height={'calc(100% - 5px)'} style={{ width: `${productCardWidth}px` }} />
                  </div>
                </div>
                <div
                  className={cx('recent-live-streaming__timeline', {
                    'recent-live-streaming__timeline--visible':
                      !isToday(new Date(formatISO(new Date(data?.liveTime)))) && activeIndex !== index && displayDateIndex.includes(index),
                  })}
                ></div>
              </SwiperSlide>
            ))}

            <div className={cx('fixed-date')}>
              <div className={cx('fixed-date__content', { 'fixed-date__content--active': hideSkeleton })}>
                {isToday(new Date(formatISO(new Date(dateList[activeIndex]))))
                  ? t('Common_Today')
                  : format(new Date(formatISO(new Date(dateList[activeIndex]))), 'M/d')}
              </div>
              <div className={cx('fixed-date__content-skeleton', { 'fixed-date__content-skeleton--active': !hideSkeleton })}>
                <Skeleton width={'5em'} />
              </div>
            </div>
          </Swiper>
          <div className={cx('navigation-btn', 'navigation-btn-prev', { 'navigation-btn--active': activeIndex !== 0 && hideSkeleton })}>
            <Icon.ArrowLeftWithoutSize />
          </div>
          <div className={cx('navigation-btn', 'navigation-btn-next', { 'navigation-btn--active': !slideIsEnd && hideSkeleton })}>
            <Icon.ArrowRight />
          </div>
        </>
      )}
    </div>
  )
}

export default RecentLiveStreaming
